/**
 * features/testimonials/api.ts — API pública de testimonios con moderación.
 *
 * Flujo:
 *   1. Un visitante (anon) envía un testimonio desde el sitio.
 *   2. La fila se inserta con `is_published = false` (pendiente / no aprobado).
 *      La RLS `testimonials_public_insert` fuerza este valor y descarta
 *      cualquier intento de auto-publicación desde el cliente.
 *   3. El admin lo modera desde `/admin/catalogos` → tab Testimonios usando
 *      `useApproveTestimonial()` (ver `features/admin/catalogs-api.ts`).
 *   4. Al aprobar (`is_published = true`), aparece en `useTestimonials()` de
 *      Home y en `usePublishedTestimonials()` (catálogo completo público).
 *
 * Convenciones (alineadas con `home/api.ts` y `admin/catalogs-api.ts`):
 *   - Types derivados directamente de `Database['public']['Tables']['testimonials']`
 *     para evitar drift con el schema.
 *   - `queryKey` público bajo `['testimonials', 'public']` (el admin usa
 *     `['admin', 'catalogs', 'testimonials']`).
 *   - Mutación pública invalida ambos scopes para que el admin vea el
 *     nuevo pendiente al instante.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type TestimonialRow = Database['public']['Tables']['testimonials']['Row'];

/**
 * Fila publicada visible al público. Excluye columnas administrativas
 * (`sort`, `is_published`, `deleted_at`, `updated_at`, `related_*`) porque
 * el consumidor público no las necesita.
 */
export interface PublicTestimonial {
  id: TestimonialRow['id'];
  /** = `author_name` en la tabla. */
  author_name: TestimonialRow['author_name'];
  avatar_url: TestimonialRow['avatar_url'];
  /** = `body` en la tabla (contenido del testimonio). */
  body: TestimonialRow['body'];
  /** Calificación de 1 a 5 estrellas (nullable). */
  rating: TestimonialRow['rating'];
  created_at: TestimonialRow['created_at'];
}

/**
 * Datos que un visitante envía desde el formulario público.
 *
 * NO incluye `is_published` — el servidor lo fuerza a `false` vía RLS. Si el
 * cliente intenta setearlo a `true`, el INSERT falla con
 * `new row violates row-level security policy`.
 */
export interface PublicTestimonialInput {
  /** Nombre del cliente. Requerido, se recorta y valida no vacío. */
  author_name: string;
  /** Contenido del testimonio. Requerido, no vacío. */
  body: string;
  /** Calificación 1–5. Opcional (algunos testimonios son solo texto). */
  rating?: number | null;
  /** URL opcional a un avatar externo. */
  avatar_url?: string | null;
  /** UUID opcional de terapia asociada. */
  related_therapy_id?: string | null;
  /** UUID opcional de producto asociado. */
  related_product_id?: string | null;
}

/**
 * Error de validación local antes de tocar la red. La forma coincide con
 * la usada en `admin/gallery-events-api.ts` y `admin/resources-api.ts`.
 */
export interface TestimonialValidation {
  ok: boolean;
  errors: Partial<Record<keyof PublicTestimonialInput, string>>;
}

// ---------------------------------------------------------------------------
// Validación pura
// ---------------------------------------------------------------------------

/**
 * Chequeos mínimos antes del INSERT. Duplica lo que la RLS ya valida,
 * pero al hacerlo local damos feedback inmediato al usuario en vez de
 * un mensaje "row-level security policy" genérico.
 */
export function validatePublicTestimonial(input: PublicTestimonialInput): TestimonialValidation {
  const errors: TestimonialValidation['errors'] = {};

  if (!input.author_name || !input.author_name.trim()) {
    errors.author_name = 'Ingresa tu nombre.';
  } else if (input.author_name.trim().length > 120) {
    errors.author_name = 'El nombre es demasiado largo (máx 120 caracteres).';
  }

  if (!input.body || !input.body.trim()) {
    errors.body = 'Escribe tu experiencia.';
  } else if (input.body.trim().length > 2000) {
    errors.body = 'El testimonio es demasiado largo (máx 2000 caracteres).';
  }

  if (input.rating !== undefined && input.rating !== null) {
    if (
      !Number.isFinite(input.rating) ||
      !Number.isInteger(input.rating) ||
      input.rating < 1 ||
      input.rating > 5
    ) {
      errors.rating = 'La calificación debe estar entre 1 y 5.';
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

// ---------------------------------------------------------------------------
// Función pura de submisión (útil en scripts / edge / tests)
// ---------------------------------------------------------------------------

/**
 * Inserta un testimonio como pendiente. Devuelve `{ ok: true }` en lugar del
 * `id` porque la RLS pública NO puede leer `is_published=false` (política
 * SELECT lo oculta). Si encadenáramos `.select('id').single()`, Supabase
 * enviaría `Prefer: return=representation` y PostgREST reportaría el INSERT
 * como "violates row-level security" (aunque la fila SÍ quede insertada) —
 * comportamiento contra-intuitivo pero documentado en el SDK.
 *
 * Uso típico desde el hook `useSubmitTestimonial`; expuesto también como
 * función pura para pruebas / edge functions. Si un caller necesita el
 * `id` (para tracking), debe usar service_role o esperar a que el admin
 * apruebe y consultar `usePublishedTestimonials`.
 */
export async function submitPublicTestimonial(
  input: PublicTestimonialInput,
): Promise<{ ok: true }> {
  const v = validatePublicTestimonial(input);
  if (!v.ok) {
    const msg = Object.values(v.errors).filter(Boolean).join(' · ');
    throw new Error(msg || 'Datos inválidos.');
  }

  // El servidor fuerza is_published=false vía RLS `testimonials_public_insert`.
  // Aquí lo enviamos explícito para dejar el intent claro al lector.
  const payload = {
    author_name: input.author_name.trim(),
    body: input.body.trim(),
    rating: input.rating ?? null,
    avatar_url: input.avatar_url?.trim() || null,
    related_therapy_id: input.related_therapy_id ?? null,
    related_product_id: input.related_product_id ?? null,
    is_published: false as const,
    sort: 0,
  };

  // OJO: no encadenar `.select()` — ver docstring.
  const { error } = await supabase.from('testimonials').insert(payload);

  if (error) throw new Error(error.message);
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Hook: enviar testimonio (público)
// ---------------------------------------------------------------------------

/**
 * Hook para el formulario público de envío de testimonios. Invalida las
 * queries del admin para que el nuevo pendiente aparezca sin necesidad de
 * refrescar la pestaña.
 */
export function useSubmitTestimonial(): UseMutationResult<
  { ok: true },
  Error,
  PublicTestimonialInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitPublicTestimonial,
    onSuccess: () => {
      // Admin: nuevo pendiente en la tabla.
      qc.invalidateQueries({ queryKey: ['admin', 'catalogs', 'testimonials'] });
      // Público: no cambia visible hasta que el admin apruebe, pero
      // invalidamos por consistencia.
      qc.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Hook: listar testimonios publicados (catálogo completo público)
// ---------------------------------------------------------------------------

/**
 * Todos los testimonios aprobados y no borrados. Diferente de
 * `useTestimonials()` en `home/api.ts` que limita a 3 para el hero de Home.
 *
 * Orden: `sort asc, created_at desc` (mismo criterio que Home).
 */
export function usePublishedTestimonials(): UseQueryResult<PublicTestimonial[], Error> {
  return useQuery<PublicTestimonial[], Error>({
    queryKey: ['testimonials', 'public'],
    queryFn: async (): Promise<PublicTestimonial[]> => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('id, author_name, avatar_url, body, rating, created_at')
        // Redundante con la RLS `testimonials_public_select`, pero explícito
        // para lectores del código.
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('sort', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as PublicTestimonial[];
    },
    staleTime: 60_000,
  });
}
